import { classMerge } from '@/chassis/core/common-helpers/class-merge';
import '@/chassis/ui/assets/css/vendors/toastify.css';
import { useRef, createRef, useEffect } from 'react';
import Toastify, { Options } from 'toastify-js';
import { Lucide } from '@/chassis/ui/components';

export interface ToastElement extends HTMLDivElement {
    toastify: ReturnType<typeof Toastify>;
    showToast: () => void;
    hideToast: () => void;
}

export interface ToastProps extends React.PropsWithChildren, React.ComponentPropsWithoutRef<'div'> {
    options?: Options;
    getRef?: (el: ToastElement) => void;
}

const toastifyClass = '_' + Math.random().toString(36).substr(2, 9);

const init = (el: ToastElement, props: ToastProps) => {
    el.showToast = () => {
        const clonedEl = el.cloneNode(true) as ToastElement;
        clonedEl.classList.remove('hidden');
        clonedEl.classList.add(toastifyClass);
        clonedEl.toastify = Toastify({
            duration: props.options?.duration ?? 4000,
            newWindow: true,
            close: false,
            gravity: 'top',
            position: 'right',
            stopOnFocus: true,
            ...props.options,
            node: clonedEl,
        });
        clonedEl.toastify.showToast();
        clonedEl.querySelectorAll("[data-dismiss='toast']").forEach((dismissEl) => {
            dismissEl.addEventListener('click', () => {
                clonedEl.toastify.hideToast();
            });
        });

        el.hideToast = () => {
            document.querySelectorAll(`.${toastifyClass}`).forEach((toastEl) => {
                const toastifyEl = toastEl as ToastElement;
                toastifyEl.toastify.hideToast();
            });
        };
    };
};

const reInit = (el: ToastElement) => {
    const wrapperEl = document.querySelectorAll(`.${toastifyClass}`)[0];
    if (wrapperEl) {
        wrapperEl.innerHTML = el.innerHTML;
    }
};

function Toast({ className = '', options = {}, getRef = () => {}, children, ...computedProps }: ToastProps) {
    const props = { className, options, getRef };
    const initialRender = useRef(true);
    const toastifyRef = createRef<ToastElement>();

    useEffect(() => {
        if (toastifyRef.current) {
            if (initialRender.current) {
                props.getRef(toastifyRef.current);
                init(toastifyRef.current, props);
                initialRender.current = false;
            } else {
                reInit(toastifyRef.current);
            }
        }
    }, [props.options, children]);

    return (
        <div
            {...computedProps}
            className={classMerge([
                'py-5 pl-5 pr-6 bg-white border border-slate-200/60 rounded-lg shadow-xl dark:bg-darkmode-600 dark:text-slate-300 dark:border-darkmode-600 hidden',
                props.className,
            ])}
            ref={toastifyRef}
        >
            {children}
        </div>
    );
}
const Success = ({ className = '', options = {}, children, ...props }: ToastProps) => {
    const mergedOptions = { className: classMerge(['toastify-success', options.className || '']), ...options };
    const mergedClassName = classMerge(['bg-emerald-50 text-emerald-800 border-emerald-200', className]);
    return (
        <Toast {...props} className={mergedClassName} options={mergedOptions}>
            <div className="flex items-center gap-2 text-sm font-medium">
                <Lucide icon="CheckCircle" className="h-4 w-4" />
                <span>{children}</span>
            </div>
        </Toast>
    );
};

type ToastComponent = typeof Toast & { Success: typeof Success };

const ToastWithVariants: ToastComponent = Object.assign(Toast, { Success });

export default ToastWithVariants;